import { bisector } from 'd3-array';
import { differenceInCalendarDays } from 'date-fns';

import { Domain } from '~/types/axis.types';
import { CreateChartOptionsWithColors } from '~/types/create.types';
import { TimeSeriesItem, TsDataPoint } from '~/types/timeseries.types';

import { RangeHorizon } from '../';
import { getStartOfRangeHorizon } from './dates';

export interface IndexedChartBar extends TimeSeriesItem {
  index: number;
}

export const getBarColor = (
  bar: IndexedChartBar,
  options: CreateChartOptionsWithColors
) =>
  bar.open <= bar.close ? options.colors.bars.up : options.colors.bars.down;

export const mapBars = (
  bars: TimeSeriesItem[],
  data: TsDataPoint[],
  domain: Domain
) => {
  const dataByTime = new Map<string, TsDataPoint & { index: number }>();
  for (let i = 0; i <= domain[1]; i++) {
    dataByTime.set(data[i].time, { ...data[i], index: i });
  }

  const result: IndexedChartBar[] = [];

  bars.forEach(bar => {
    const tsData = dataByTime.get(bar.time);
    if (tsData) {
      result.push({ index: tsData.index, ...bar });
    }
  });

  return result;
};

export const extendData = (data: TsDataPoint[], daysToAdd = MAX_RANGE) => {
  const ts: TsDataPoint[] = [...data];
  const lastIdx = data.length - 1;
  const endDate = new Date(data[lastIdx].time);
  const weekend = [0, 6];
  const extraDays = Math.min(Math.round(data.length * 2), daysToAdd);

  let lastKnownDate = endDate;
  let i = 0;

  while (i < extraDays) {
    lastKnownDate = new Date(lastKnownDate);
    lastKnownDate.setUTCDate(lastKnownDate.getUTCDate() + 1);
    if (!weekend.includes(lastKnownDate.getUTCDay())) {
      lastKnownDate.setUTCHours(0, 0, 0, 0);
      ts.push({ time: lastKnownDate.toISOString(), value: NaN });
      i++;
    }
  }

  return ts;
};

const MIN_DAYS_OFFSET = 2;
const MAX_RANGE = 100;
const DATES_PADDING_RIGHT = 0.03;

export const getDomainFromHorizon = (
  data: TsDataPoint[],
  horizon: RangeHorizon,
  lastIdx: number
): Domain => {
  if (horizon === RangeHorizon.OneDay) {
    return [lastIdx - 1, lastIdx + 0.5];
  }

  const endDate = new Date(data[lastIdx].time);
  const start = getStartOfRangeHorizon(horizon, endDate);
  const max = Math.min(Math.round(DATES_PADDING_RIGHT * lastIdx), MAX_RANGE);

  if (!start) {
    return [0, lastIdx + Math.max(MIN_DAYS_OFFSET, max)];
  } else {
    const diff = differenceInCalendarDays(endDate, start);
    const daysToAdd = Math.max(
      MIN_DAYS_OFFSET,
      Math.min(Math.round(DATES_PADDING_RIGHT * diff), MAX_RANGE)
    );
    const endIdx = lastIdx + daysToAdd;

    const finalEndDate = new Date(data[endIdx].time);
    const utcStart = getStartOfRangeHorizon(horizon, finalEndDate);
    const bis = bisector((d: TsDataPoint) => new Date(d.time));
    const startIdx = utcStart ? bis.left(data, new Date(utcStart)) : 0;

    return [startIdx, endIdx];
  }
};
